import React, { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useBrand, splitBrandName } from '@/hooks/useBrand'
import api from '@/lib/api'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { ArrowRight, X as XIcon } from 'lucide-react'
import { AuthShell, EASE_OUT_STRONG } from '@/components/auth/AuthShell'

interface OrgOption {
  id: string
  name: string
  slug: string
  role: string
  status: string
  subscriptionTier: string
}

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetSent, setResetSent] = useState(false)
  const [showOrgPicker, setShowOrgPicker] = useState(false)
  const [orgs, setOrgs] = useState<OrgOption[]>([])

  const { login, selectOrganization, isAuthenticated, agent } = useAuth()
  const brand = useBrand()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const justDeleted = searchParams.get('deleted') === '1'

  // Match <html> AND <body> background to the login gradient so iOS
  // rubber-band overscroll (and short-content scroll) don't flash the
  // light app background below the card. Body has bg-background applied
  // globally in index.css, so we override both.
  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    const prevHtml = html.style.backgroundColor
    const prevBody = body.style.backgroundColor
    html.style.backgroundColor = '#0d0a1f'
    body.style.backgroundColor = '#0d0a1f'
    return () => {
      html.style.backgroundColor = prevHtml
      body.style.backgroundColor = prevBody
    }
  }, [])

  // After login, honor ?redirect=... so flows like /redeem?code=... can
  // bounce the user through the login page and land back on the right
  // screen. Restricted to same-origin paths so an attacker can't craft
  // a login URL that punts to an external site post-auth.
  const redirectParam = searchParams.get('redirect') || ''
  const safeRedirect = redirectParam.startsWith('/') && !redirectParam.startsWith('//')
    ? redirectParam
    : '/'

  // Redirect to dashboard (or the requested redirect) when authenticated
  // (but not if org picker is showing)
  useEffect(() => {
    if (isAuthenticated && !showOrgPicker) {
      navigate(safeRedirect, { replace: true })
    }
  }, [isAuthenticated, showOrgPicker, navigate, safeRedirect])

  // Where to land after auth. Franchise (reseller) admins go straight to
  // their console; everyone else gets the dashboard. An explicit ?redirect=
  // always wins. Platform admins (root) are excluded — they keep the normal
  // dashboard/Castle flow even though whoami also reports them as franchise
  // admins.
  const resolveLandingDest = async (): Promise<string> => {
    if (safeRedirect !== '/') return safeRedirect
    try {
      const agentData = JSON.parse(localStorage.getItem('app_agent_data') || '{}')
      if (!agentData.isPlatformAdmin) {
        const who = await api.get('/api/franchise/whoami')
        if (who.data?.isFranchiseAdmin) return '/franchise/clients'
      }
    } catch {
      // fall back to the dashboard on any error
    }
    return '/'
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    const result = await login(email, password)

    if (!result || !result.success) {
      setError(result?.message || 'Login failed')
      setIsLoading(false)
    } else if ((result as any).requiresOrgSelection && (result as any).organizations?.length > 1) {
      setOrgs((result as any).organizations)
      setShowOrgPicker(true)
      setIsLoading(false)
    } else {
      window.location.href = await resolveLandingDest()
    }
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const response = await api.post(`/api/auth/forgot-password`, { email: resetEmail })

      if (response.data.success !== false) {
        setResetSent(true)
      } else {
        setError(response.data.message || 'Failed to send reset email')
      }
    } catch {
      setError('Failed to connect to server')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSelectOrg = async (orgId: string) => {
    setIsLoading(true)
    setError('')
    const result = await selectOrganization(orgId)
    if (result.success) {
      window.location.href = await resolveLandingDest()
    } else {
      setError(result.message || 'Failed to switch organization')
      setIsLoading(false)
    }
  }

  // ============================================================================
  //   ORG PICKER VIEW
  // ============================================================================
  if (showOrgPicker) {
    return (
      <AuthShell
        headline={<>Pick up where you left off.</>}
        sub={`You belong to multiple organizations on ${splitBrandName(brand.appName)[0]}. Choose one to continue.`}
      >
        <div
          className="opacity-0 animate-fade-in-up"
          style={{ animationDelay: '80ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
        >
          <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight mb-2">Select account</h2>
          <p className="text-white/60 text-sm mb-6">Which organization would you like to sign into?</p>

          {error && (
            <div className="mb-4 bg-red-500/10 border border-red-400/20 rounded-xl p-3 animate-in fade-in slide-in-from-top-1 duration-200">
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-2.5">
            {(agent as any)?.isPlatformAdmin && (
              <button
                onClick={() => { window.location.href = '/admin' }}
                disabled={isLoading}
                className="group w-full text-left p-4 rounded-xl border border-brand-royal/30 bg-brand-royal/10 hover:bg-brand-royal/15 hover:border-brand-royal/60 transition-[background-color,border-color,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.99] disabled:opacity-50"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-white font-medium group-hover:text-brand-periwinkle transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]">
                      The Castle
                    </p>
                    <p className="text-xs text-white/50 mt-0.5">Above every franchise</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-brand-royal/60 group-hover:text-brand-royal group-hover:translate-x-0.5 transition-[color,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] shrink-0" strokeWidth={2} />
                </div>
              </button>
            )}
            {orgs.map((org, idx) => (
              <button
                key={org.id}
                onClick={() => handleSelectOrg(org.id)}
                disabled={isLoading}
                className="group w-full text-left p-4 rounded-xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] hover:border-brand-royal/50 transition-[background-color,border-color,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.99] disabled:opacity-50 opacity-0 animate-fade-in-up"
                style={{
                  animationDelay: `${120 + idx * 50}ms`,
                  animationFillMode: 'forwards',
                  animationTimingFunction: EASE_OUT_STRONG,
                }}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-white font-medium truncate group-hover:text-brand-periwinkle transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]">
                      {org.name}
                    </p>
                    <p className="text-xs text-white/50 mt-0.5">
                      {org.role === 'admin' ? 'Administrator' : org.role === 'supervisor' ? 'Supervisor' : 'Team Member'}
                      {' \u00b7 '}
                      {org.subscriptionTier === 'trial' ? 'Trial' : org.subscriptionTier}
                    </p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-white/30 group-hover:text-brand-royal group-hover:translate-x-0.5 transition-[color,transform] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] shrink-0" strokeWidth={2} />
                </div>
              </button>
            ))}
          </div>

          {isLoading && (
            <div className="flex justify-center mt-6">
              <LoadingSpinner size="sm" />
            </div>
          )}
        </div>
      </AuthShell>
    )
  }

  // ============================================================================
  //   FORGOT PASSWORD VIEW
  // ============================================================================
  if (showForgotPassword) {
    return (
      <AuthShell
        headline={<>Let's get you<br />back in.</>}
        sub="We'll email you a link to reset your password. Check spam if it doesn't show up in a minute."
      >
        <div
          className="opacity-0 animate-fade-in-up"
          style={{ animationDelay: '80ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
        >
          <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight mb-2">Reset password</h2>
          <p className="text-white/60 text-sm mb-6">Enter the email you signed up with.</p>

          {resetSent ? (
            <div className="space-y-5">
              <div className="bg-emerald-500/10 border border-emerald-400/25 rounded-xl p-4 animate-in fade-in zoom-in-95 duration-300 ease-[cubic-bezier(0.23,1,0.32,1)]">
                <p className="text-emerald-200 text-sm leading-relaxed">
                  If an account exists for <span className="font-medium">{resetEmail}</span>, reset instructions are on
                  their way to your inbox.
                </p>
              </div>
              <button
                onClick={() => {
                  setShowForgotPassword(false)
                  setResetSent(false)
                  setResetEmail('')
                }}
                className="inline-flex items-center gap-1.5 py-2 -my-2 text-brand-periwinkle hover:text-white text-sm font-medium transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.98]"
              >
                <ArrowRight className="h-4 w-4 rotate-180" strokeWidth={2} />
                Back to sign in
              </button>
            </div>
          ) : (
            <form className="space-y-5" onSubmit={handleForgotPassword}>
              <div>
                <label htmlFor="reset-email" className="block text-sm font-medium text-white/75 mb-2">
                  Email address
                </label>
                <input
                  id="reset-email"
                  name="email"
                  type="email"
                  required
                  autoFocus
                  className="block w-full h-12 px-4 border border-white/10 rounded-xl placeholder-white/30 text-white bg-white/[0.04] focus:outline-none focus:ring-2 focus:ring-brand-royal/70 focus:border-transparent text-[15px] transition-[border-color,box-shadow,background-color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)]"
                  placeholder="you@lawfirm.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                />
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-400/20 rounded-xl p-3 animate-in fade-in slide-in-from-top-1 duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]">
                  <p className="text-red-300 text-sm">{error}</p>
                </div>
              )}

              <div className="flex flex-col gap-2 pt-2">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center items-center h-12 px-4 text-[15px] font-semibold rounded-xl text-white bg-gradient-to-r from-brand-royal to-brand-periwinkle hover:brightness-110 shadow-lg shadow-brand-royal/30 hover:shadow-brand-royal/50 disabled:opacity-50 disabled:cursor-not-allowed transition-[transform,box-shadow,filter] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] disabled:active:scale-100 border-0"
                >
                  {isLoading ? <LoadingSpinner size="sm" /> : 'Send reset link'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="inline-flex items-center gap-1.5 justify-center text-white/55 hover:text-white text-sm font-medium py-2 transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.98]"
                >
                  <ArrowRight className="h-4 w-4 rotate-180" strokeWidth={2} />
                  Back to sign in
                </button>
              </div>
            </form>
          )}
        </div>
      </AuthShell>
    )
  }

  // ============================================================================
  //   SIGN-IN VIEW (primary)
  // ============================================================================
  return (
    <AuthShell
      headline={<>Welcome<br />back.</>}
      sub="Sign in to pick up where you left off — your calls, chats, and bookings are waiting."
    >
      <div
        className="opacity-0 animate-fade-in-up"
        style={{ animationDelay: '80ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
      >
        <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight mb-2">Sign in</h2>
        <p className="text-white/60 text-sm mb-6">
          New here?{' '}
          <Link
            to="/onboarding"
            className="text-brand-periwinkle hover:text-white font-medium transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]"
          >
            Get started
          </Link>
          <span className="text-white/30 mx-2">·</span>
          <Link
            to="/pricing"
            className="text-brand-periwinkle hover:text-white font-medium transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]"
          >
            See pricing
          </Link>
        </p>

        {justDeleted && (
          <div className="mb-5 bg-emerald-500/10 border border-emerald-400/25 rounded-xl p-3.5 flex items-start justify-between gap-3 animate-in fade-in slide-in-from-top-1 duration-300 ease-[cubic-bezier(0.23,1,0.32,1)]">
            <p className="text-emerald-200 text-sm leading-relaxed">
              Your account and all data were permanently deleted.
            </p>
            <button
              type="button"
              onClick={() => setSearchParams({})}
              className="-m-1.5 p-1.5 rounded-md text-emerald-300/60 hover:text-emerald-200 hover:bg-emerald-500/10 shrink-0 transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-90"
              aria-label="Dismiss"
            >
              <XIcon className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>
        )}

        <form className="space-y-4" onSubmit={handleSubmit}>
          <fieldset disabled={isLoading} className="space-y-4 border-0 p-0 m-0 min-w-0">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white/75 mb-2">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="block w-full h-12 px-4 border border-white/10 rounded-xl placeholder-white/30 text-white bg-white/[0.04] focus:outline-none focus:ring-2 focus:ring-brand-royal/70 focus:border-transparent text-[15px] transition-[border-color,box-shadow,background-color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)]"
                placeholder="you@lawfirm.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <div className="flex items-baseline justify-between mb-2">
                <label htmlFor="password" className="block text-sm font-medium text-white/75">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="-my-1 py-1 text-xs font-medium text-brand-periwinkle hover:text-white transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]"
                >
                  Forgot password?
                </button>
              </div>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="block w-full h-12 px-4 border border-white/10 rounded-xl placeholder-white/30 text-white bg-white/[0.04] focus:outline-none focus:ring-2 focus:ring-brand-royal/70 focus:border-transparent text-[15px] transition-[border-color,box-shadow,background-color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)]"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-400/20 rounded-xl p-3 animate-in fade-in slide-in-from-top-1 duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]">
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center items-center h-12 px-4 text-[15px] font-semibold rounded-xl text-white bg-gradient-to-r from-brand-royal to-brand-periwinkle hover:brightness-110 shadow-lg shadow-brand-royal/30 hover:shadow-brand-royal/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#0d0a1f] focus:ring-brand-royal disabled:opacity-50 disabled:cursor-not-allowed transition-[transform,box-shadow,filter] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] disabled:active:scale-100 border-0"
            >
              {isLoading ? <LoadingSpinner size="sm" /> : 'Sign in'}
            </button>
          </fieldset>
        </form>

        {/* Tiny mobile footnote — desktop users see the value props on the left panel */}
        <p className="lg:hidden text-center text-xs text-white/35 mt-8 leading-relaxed px-4">
          Trusted by law firms for front-desk calls, lead capture, and client intake.
        </p>
      </div>

      {/* Affiliate-program entry point — discrete bottom-right anchor that
          sends sales/agency partners to the public application page without
          competing with the firm sign-in CTAs above. */}
      <Link
        to="/affiliate/apply"
        className="fixed bottom-5 right-5 z-20 inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white font-medium py-2 px-3 rounded-md hover:bg-white/[0.06] transition-[color,background-color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]"
      >
        Become an affiliate
        <ArrowRight className="h-3 w-3" strokeWidth={2} />
      </Link>
    </AuthShell>
  )
}
